import {range} from 'lodash';
import {DateTime} from 'luxon';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {DateTimeFormatterBase} from '../../../../libs/shared/ui/src/components/datetime/dateTimeFormatterBase';
import {GridItem} from '../../../../libs/shared/ui/src/components/grid/grid';

/*
 * Props.
 */

interface DatePickerHeaderProps {
  focusedMonth: DateTime;
  onFocusPreviousMonth: () => void;
  onFocusNextMonth: () => void;
  color: string | undefined;
  backgroundHoverColor: string | undefined;
  hasForwardMonthButton?: boolean;
  hasBackMonthButton?: boolean;
}

/*
 * Style.
 */

const StyledCalendarHeader = styled.header`
  display: grid;
  grid-auto-columns: auto;
  grid-template-rows: auto;
  justify-content: center;
  justify-items: center;
  align-items: center;
`;
interface TitleStyleProps {
  $color?: string;
}
const StyledTitleDiv = styled.div<TitleStyleProps>`
  grid-column: 2;
  grid-row: 1;
  margin: 0 5px;
  color: ${(p) => p.$color || p.theme.greys.shade70};
  text-transform: capitalize;
`;

const StyledInvisibleDiv = styled(StyledTitleDiv)`
  opacity: 0;
`;

/*
 * Component.
 */

export const DatePickerHeader: FC<DatePickerHeaderProps> = (props) => {
  const {
    focusedMonth,
    onFocusPreviousMonth,
    onFocusNextMonth,
    color,
    backgroundHoverColor,
    hasForwardMonthButton = true,
    hasBackMonthButton = true,
  } = props;
  return (
    <StyledCalendarHeader>
      <GridItem column={1}>
        {hasBackMonthButton ? (
          <IconButton
            iconName="chevronLeftSmall"
            label={<Trans id="datepicker-previousmonth">Previous month</Trans>}
            onClick={onFocusPreviousMonth}
            color={color}
            hoverColor={color}
            backgroundHoverColor={backgroundHoverColor}
          />
        ) : null}
      </GridItem>
      {renderAllMonths(focusedMonth, color)}
      {renderFocusedMonth(focusedMonth, color)}
      <GridItem column={3}>
        {hasForwardMonthButton ? (
          <IconButton
            iconName="chevronRightSmall"
            label={<Trans id="datepicker-nextmonth">Next month</Trans>}
            onClick={onFocusNextMonth}
            color={color}
            hoverColor={color}
            backgroundHoverColor={backgroundHoverColor}
          />
        ) : null}
      </GridItem>
    </StyledCalendarHeader>
  );
};

/** Render all months in the year at opacity 0 for spacing. */
function renderAllMonths(focusedMonth: DateTime, color: string | undefined) {
  return range(1, 13).map((m) => {
    const month = focusedMonth.set({month: m});
    return (
      <StyledInvisibleDiv $color={color} key={m} aria-hidden>
        {renderMonth(month)}
      </StyledInvisibleDiv>
    );
  });
}

/** Render the selected month. */
function renderFocusedMonth(focusedMonth: DateTime, color: string | undefined) {
  return (
    <StyledTitleDiv $color={color} data-testid="focusedMonth">
      {renderMonth(focusedMonth)}
    </StyledTitleDiv>
  );
}

/** Localization-aware renderer for a single month. */
function renderMonth(month: DateTime) {
  return <DateTimeFormatterBase value={month} format={{year: 'numeric', month: 'long'}} />;
}
